/**
 * Historial de rollbacks (ADR_012 PR-CMgmt-I).
 *
 * Tabla del audit log con:
 *   - Badges colorido por status: pending_apply (ámbar), applied (verde),
 *     failed (rojo), cancelled (gris)
 *   - Columnas: timestamp, kind (individual/masivo), device, status,
 *     retries, último error
 *   - Filtro por status
 *   - Paginación
 *
 * Ruta: /admin/configuracion/rollbacks
 */
import { useState } from "react";
import { AlertTriangle, CheckCircle2, Clock, XCircle } from "lucide-react";

import type { RollbackAuditEntry, RollbackStatus } from "@/api/types";
import { useRollbackHistory } from "@/hooks/useRollback";
import { cn } from "@/lib/utils";

const STATUS_FILTER_OPTIONS: { value: "" | RollbackStatus; label: string }[] = [
  { value: "", label: "Todos" },
  { value: "pending_apply", label: "Pendiente" },
  { value: "applied", label: "Aplicado" },
  { value: "failed", label: "Fallido" },
  { value: "cancelled", label: "Cancelado" },
];

function StatusBadge({ status }: { status: RollbackStatus }) {
  const config: Record<
    RollbackStatus,
    { label: string; cls: string; Icon: typeof Clock }
  > = {
    pending_apply: {
      label: "Pendiente",
      cls: "bg-amber-500/15 text-amber-800 dark:text-amber-400",
      Icon: Clock,
    },
    applied: {
      label: "Aplicado",
      cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
      Icon: CheckCircle2,
    },
    failed: {
      label: "Fallido",
      cls: "bg-red-500/15 text-red-700 dark:text-red-400",
      Icon: XCircle,
    },
    cancelled: {
      label: "Cancelado",
      cls: "bg-muted text-muted-foreground",
      Icon: AlertTriangle,
    },
  };
  const c = config[status];
  const Icon = c.Icon;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium",
        c.cls,
      )}
    >
      <Icon className="size-3" />
      {c.label}
    </span>
  );
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("es-MX", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function shortId(id: string): string {
  return id.slice(0, 8);
}

export function RollbacksHistoryPage() {
  const [statusFilter, setStatusFilter] = useState<"" | RollbackStatus>("");
  const [page, setPage] = useState(1);
  const perPage = 20;

  const query = useRollbackHistory({
    status: statusFilter || undefined,
    page,
    perPage,
  });

  const items: RollbackAuditEntry[] = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">
          Historial de rollbacks
        </h1>
        <p className="text-sm text-muted-foreground">
          Auditoría de cada solicitud de rollback. El worker async aplica las
          pendientes y actualiza el estado.
        </p>
      </header>

      <div className="flex items-center gap-2">
        <select
          aria-label="Filtrar por estado"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as "" | RollbackStatus);
            setPage(1);
          }}
          className="rounded-md border bg-background px-3 py-1.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          {STATUS_FILTER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <span className="flex-1" />
        <span className="text-xs text-muted-foreground">
          {total} {total === 1 ? "rollback" : "rollbacks"}
        </span>
      </div>

      <div className="overflow-hidden rounded-md border bg-card">
        <div className="grid grid-cols-12 gap-3 border-b bg-muted/30 px-4 py-2 text-xs font-medium uppercase text-muted-foreground">
          <div className="col-span-3">Solicitado</div>
          <div className="col-span-2">Tipo</div>
          <div className="col-span-2">Device</div>
          <div className="col-span-2">Estado</div>
          <div className="col-span-1 text-right">Reintentos</div>
          <div className="col-span-2">Último error</div>
        </div>

        {query.isPending && items.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">
            Cargando…
          </div>
        )}

        {!query.isPending && items.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">
            No hay rollbacks registrados.
          </div>
        )}

        {items.map((entry) => (
          <div
            key={entry.id}
            className="grid grid-cols-12 items-center gap-3 border-b px-4 py-3 text-sm last:border-b-0"
          >
            <div className="col-span-3 text-xs">
              <div className="font-medium">
                {formatDateTime(entry.requested_at)}
              </div>
              {entry.applied_at && (
                <div className="text-muted-foreground">
                  Aplicado: {formatDateTime(entry.applied_at)}
                </div>
              )}
            </div>
            <div className="col-span-2">
              <span className="rounded-md bg-muted/50 px-2 py-0.5 text-xs">
                {entry.kind === "device" ? "Individual" : "Masivo"}
              </span>
              {entry.profile_id && (
                <div className="mt-0.5 font-mono text-[10px] text-muted-foreground">
                  perfil {shortId(entry.profile_id)}
                </div>
              )}
            </div>
            <div className="col-span-2 font-mono text-xs">
              {shortId(entry.device_id)}
            </div>
            <div className="col-span-2">
              <StatusBadge status={entry.status} />
            </div>
            <div className="col-span-1 text-right text-xs">
              {entry.retry_count > 0 ? (
                <span className="font-medium">{entry.retry_count}/3</span>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </div>
            <div className="col-span-2 truncate text-xs text-muted-foreground">
              {entry.last_error ?? "—"}
            </div>
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="rounded-md border px-3 py-1.5 text-xs hover:bg-muted disabled:opacity-50"
          >
            Anterior
          </button>
          <span className="text-xs text-muted-foreground">
            Página {page} de {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="rounded-md border px-3 py-1.5 text-xs hover:bg-muted disabled:opacity-50"
          >
            Siguiente
          </button>
        </div>
      )}
    </div>
  );
}
